'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Briefcase, MapPin, Users, Clock } from 'lucide-react';
import { VolunteerOpportunity, VolunteerOpportunityFrequency } from '@/types';
import CreateVolunteerModal from '@/components/ui/CreateVolunteerModal';
import { useLanguage } from '@/context/LanguageContext';
import { fetchStrapiCollection } from '@/lib/strapi';

interface JobsTabProps {
  groupId: number;
  groupName: string;
  canPost: boolean;
}

export function JobsTab({ groupId, groupName: _groupName, canPost }: JobsTabProps) {
  const { t } = useLanguage();
  const [jobs, setJobs] = useState<VolunteerOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return t.time.justNow;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}${t.time.minutesAgo}`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}${t.time.hoursAgo}`;
    return `${Math.floor(diffInSeconds / 86400)}${t.time.daysAgo}`;
  };

  const fetchJobs = useCallback(async () => {
    try {
      const rawJobs = await fetchStrapiCollection<{ id: number; documentId: string; title: string; organization: string; location: string; volunteersNeeded: number; applicantCount?: number; frequency: VolunteerOpportunityFrequency; salary?: string; image?: { url: string }; createdAt: string; updatedAt: string }>('volunteer-opportunities', {
        filters: { group: { id: { $eq: groupId } }, deletedAt: { $null: true } },
        populate: 'image',
        sort: 'createdAt:desc',
      });
      setJobs(rawJobs.map((item) => ({
        id: item.id,
        documentId: item.documentId,
        title: item.title,
        organization: item.organization,
        location: item.location,
        volunteersNeeded: item.volunteersNeeded,
        applicantCount: item.applicantCount ?? 0,
        frequency: item.frequency,
        salary: item.salary,
        image: item.image,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      })));
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    void fetchJobs();
  }, [fetchJobs]);

  if (loading) return <div className="text-center py-12 text-foreground-muted">{t.common.loading}</div>;

  return (
    <>
      <CreateVolunteerModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        preselectedGroupId={groupId}
        onResult={(success, _message) => {
          if (success) {
            setLoading(true);
            void fetchJobs();
          }
        }}
      />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground" data-testid="group-jobs-tab-heading">{t.groupTabs.jobPostings}</h2>
          {canPost && (
            <button
              onClick={() => setShowCreateModal(true)}
              data-testid="group-jobs-tab-post-job-btn"
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition"
            >
              {t.volunteers.postOpportunity}
            </button>
          )}
        </div>

      {jobs.length === 0 ? (
        <div data-testid="group-jobs-tab-empty-state" className="text-center py-12 text-foreground-muted border border-border rounded-lg bg-background-secondary">
          <Briefcase size={32} className="mx-auto mb-2 opacity-50" />
          <p>{t.volunteers.noOpportunities}</p>
          {canPost && <p className="text-sm mt-1">{t.groupTabs.checkBackForJobs}</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <Link key={job.documentId} href={`/volunteers/${job.documentId}`} data-testid={`group-jobs-tab-card-${job.documentId}`} className="block p-4 border border-border rounded-lg hover:bg-background-secondary transition">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground hover:text-primary">{job.title}</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background-secondary px-2.5 py-1 text-xs font-medium text-foreground-muted">
                      <Users size={12} />
                      {job.volunteersNeeded} {job.volunteersNeeded === 1 ? t.volunteers.position : t.volunteers.positions} {t.volunteers.needed}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background-secondary px-2.5 py-1 text-xs font-medium text-foreground-muted">
                      <Users size={12} />
                      {(job.applicantCount ?? 0)} {(job.applicantCount ?? 0) === 1 ? t.volunteers.volunteerLabel : t.volunteers.volunteersLabel}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 text-sm text-foreground-muted mt-3">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {job.location}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {t.groupTabs.posted} {getTimeAgo(job.createdAt)}</span>
                  </div>
                </div>
                <button data-testid={`group-jobs-tab-view-job-btn-${job.documentId}`} className="px-4 py-1.5 bg-primary text-white rounded-md text-sm font-medium hover:opacity-90 transition self-start sm:self-center whitespace-nowrap">{t.common.view}</button>
              </div>
            </Link>
          ))}
        </div>
      )}
      </div>
    </>
  );
}

